import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Mail,
  Plus,
  Edit,
  Trash2,
  X,
  Loader2,
  Send,
  Users,
  Eye,
  BarChart3,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Search,
  Filter,
  Image as ImageIcon,
  Upload
} from 'lucide-react';
import {
  createEmailGroup,
  getEmailGroups,
  getEmailGroup,
  updateEmailGroup,
  deleteEmailGroup,
  sendFirstEmailToEmailGroup,
  sendFollowUpEmailToEmailGroup,
  getEmailGroupsStats,
  getBusinessContacts,
  uploadEmailImages
} from '../../../redux/api';

const EmailGroupsView = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState({
    groupNumber: '',
    name: '',
    description: '',
    emailAddresses: []
  });
  const [emailInput, setEmailInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingFirstEmail, setSendingFirstEmail] = useState({});
  const [sendingFollowUp, setSendingFollowUp] = useState({});
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [emailType, setEmailType] = useState('first'); // 'first' or 'followup'
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailImages, setEmailImages] = useState([]); // Array of uploaded image URLs
  const [uploadingImages, setUploadingImages] = useState(false);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.result?.role === 'superadmin';

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error('Nemate pristup ovoj stranici');
      return;
    }
    fetchGroups();
    fetchStats();
  }, [isSuperAdmin]);

  // Refetch available contacts when groups change or when editing group changes
  useEffect(() => {
    if (isSuperAdmin && groups.length >= 0) {
      fetchAvailableContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, groups.length, editingGroup?._id]);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await getEmailGroups();
      const groupsData = response.data?.groups || [];
      setGroups(groupsData);
      
      // Debug: Log group data to see what we're getting
      console.log('📧 [DEBUG] Email Groups loaded:', groupsData.length);
      groupsData.forEach(group => {
        console.log(`  Group ${group.groupNumber} (${group.name}):`, {
          firstEmailSentAt: group.firstEmailSentAt,
          firstEmailSentCount: group.firstEmailSentCount,
          firstEmailFailedCount: group.firstEmailFailedCount,
          followUpEmailSentAt: group.followUpEmailSentAt,
          followUpEmailSentCount: group.followUpEmailSentCount,
          status: group.status
        });
      });
    } catch (error) {
      console.error('Error fetching email groups:', error);
      toast.error('Greška pri učitavanju grupa');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const response = await getEmailGroupsStats();
      setStats(response.data?.stats || null);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchAvailableContacts = async () => {
    try {
      setLoadingContacts(true);
      const response = await getBusinessContacts(1, 10000);
      const data = response.data || {};
      const allContacts = data.contacts || [];
      const contactsWithEmail = allContacts.filter(contact => 
        contact.email && contact.email.trim() !== ''
      );
      
      // Get all email addresses that are already assigned to groups
      // Exclude emails from the currently edited group (if editing)
      const assignedEmails = new Set();
      groups.forEach(group => {
        // If editing a group, exclude emails from that group so they can be edited
        if (editingGroup && group._id === editingGroup._id) {
          return; // Skip this group's emails
        }
        group.emailAddresses?.forEach(emailData => {
          if (emailData.email) {
            assignedEmails.add(emailData.email.toLowerCase().trim());
          }
        });
      });
      
      // Filter out contacts whose emails are already assigned to groups
      const availableContacts = contactsWithEmail.filter(contact => {
        const email = contact.email?.toLowerCase().trim();
        return email && !assignedEmails.has(email);
      });
      
      setAvailableContacts(availableContacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchGroupDetails = async (groupId) => {
    try {
      const response = await getEmailGroup(groupId);
      setSelectedGroup(response.data?.group || null);
    } catch (error) {
      console.error('Error fetching group details:', error);
      toast.error('Greška pri učitavanju detalja grupe');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addEmailAddress = () => {
    if (!emailInput.trim()) return;
    
    const emails = emailInput.split(/[,\n]/).map(e => e.trim()).filter(e => e);
    
    // Check for duplicates and emails already assigned to groups
    const validEmails = [];
    const invalidEmails = [];
    
    emails.forEach(email => {
      const normalizedEmail = email.toLowerCase();
      
      // Check if already in form
      const existsInForm = formData.emailAddresses.some(e => 
        e.email.toLowerCase() === normalizedEmail
      );
      
      if (existsInForm) {
        invalidEmails.push(`${email} (već postoji u grupi)`);
        return;
      }
      
      // Check if assigned to another group
      if (!editingGroup) {
        // Creating new group - check all groups
        const assignedToGroup = groups.some(group => 
          group.emailAddresses?.some(emailData => 
            emailData.email?.toLowerCase() === normalizedEmail
          )
        );
        
        if (assignedToGroup) {
          invalidEmails.push(`${email} (već dodeljen drugoj grupi)`);
          return;
        }
      } else {
        // Editing group - check other groups
        const assignedToOtherGroup = groups.some(group => 
          group._id !== editingGroup._id &&
          group.emailAddresses?.some(emailData => 
            emailData.email?.toLowerCase() === normalizedEmail
          )
        );
        
        if (assignedToOtherGroup) {
          invalidEmails.push(`${email} (već dodeljen drugoj grupi)`);
          return;
        }
      }
      
      validEmails.push({
        email,
        companyName: null
      });
    });
    
    if (invalidEmails.length > 0) {
      toast.warning(`Neki emailovi nisu dodati:\n${invalidEmails.join('\n')}`);
    }
    
    if (validEmails.length > 0) {
      if (formData.emailAddresses.length + validEmails.length > 100) {
        toast.error('Maksimum 100 email adresa po grupi');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        emailAddresses: [...prev.emailAddresses, ...validEmails]
      }));
      
      if (validEmails.length > 0) {
        toast.success(`${validEmails.length} email adresa dodato`);
      }
    }
    
    setEmailInput('');
  };

  const removeEmailAddress = (index) => {
    setFormData(prev => ({
      ...prev,
      emailAddresses: prev.emailAddresses.filter((_, i) => i !== index)
    }));
  };

  const addContactFromList = (contact) => {
    // Check if email already exists in current form
    const emailExistsInForm = formData.emailAddresses.some(e => 
      e.email.toLowerCase() === contact.email.toLowerCase()
    );
    
    if (emailExistsInForm) {
      toast.warning('Email adresa već postoji u grupi');
      return;
    }

    // Check if email is already assigned to another group (when creating new group)
    if (!editingGroup) {
      const emailAssignedToOtherGroup = groups.some(group => 
        group.emailAddresses?.some(emailData => 
          emailData.email?.toLowerCase() === contact.email.toLowerCase()
        )
      );
      
      if (emailAssignedToOtherGroup) {
        toast.warning('Email adresa je već dodeljena drugoj grupi');
        return;
      }
    } else {
      // When editing, check if email is in another group (not the one being edited)
      const emailAssignedToOtherGroup = groups.some(group => 
        group._id !== editingGroup._id &&
        group.emailAddresses?.some(emailData => 
          emailData.email?.toLowerCase() === contact.email.toLowerCase()
        )
      );
      
      if (emailAssignedToOtherGroup) {
        toast.warning('Email adresa je već dodeljena drugoj grupi');
        return;
      }
    }

    if (formData.emailAddresses.length >= 100) {
      toast.error('Maksimum 100 email adresa po grupi');
      return;
    }

    setFormData(prev => ({
      ...prev,
      emailAddresses: [...prev.emailAddresses, {
        email: contact.email,
        companyName: contact.companyName
      }]
    }));
  };

  const resetForm = () => {
    setFormData({
      groupNumber: '',
      name: '',
      description: '',
      emailAddresses: []
    });
    setEmailInput('');
    setEditingGroup(null);
    setShowForm(false);
    // Refetch available contacts when form is closed
    setTimeout(() => {
      fetchAvailableContacts();
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.groupNumber) {
      toast.error('Broj grupe je obavezan');
      return;
    }

    if (formData.emailAddresses.length === 0) {
      toast.error('Dodajte barem jednu email adresu');
      return;
    }

    if (formData.emailAddresses.length > 100) {
      toast.error('Maksimum 100 email adresa po grupi');
      return;
    }

    setSubmitting(true);
    try {
      if (editingGroup) {
        await updateEmailGroup({ id: editingGroup._id, groupData: formData });
        toast.success('Grupa je uspešno ažurirana');
      } else {
        await createEmailGroup(formData);
        toast.success('Grupa je uspešno kreirana');
      }
      resetForm();
      fetchGroups();
      fetchStats();
    } catch (error) {
      console.error('Error saving group:', error);
      toast.error(error.response?.data?.message || 'Greška pri čuvanju grupe');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      groupNumber: group.groupNumber,
      name: group.name || `Grupa ${group.groupNumber}`,
      description: group.description || '',
      emailAddresses: group.emailAddresses || []
    });
    setShowForm(true);
    // Refetch available contacts to exclude emails from this group
    setTimeout(() => {
      fetchAvailableContacts();
    }, 100);
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovu grupu?')) {
      return;
    }

    try {
      await deleteEmailGroup(groupId);
      toast.success('Grupa je uspešno obrisana');
      fetchGroups();
      fetchStats();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error(error.response?.data?.message || 'Greška pri brisanju grupe');
    }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;

    // Validate files
    const validFiles = Array.from(files).filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} nije slika`);
        return false;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name} je prevelik (max 2MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    if (emailImages.length + validFiles.length > 5) {
      toast.error('Maksimum 5 slika po emailu');
      return;
    }

    setUploadingImages(true);
    try {
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('images', file);
      });

      const response = await uploadEmailImages(formData);
      const uploadedImages = response.data?.images || [];
      
      // Get API base URL for full image URLs
      const { API_BASE_URL } = await import('../../../config/api.js');
      const imageUrls = uploadedImages.map(img => {
        // If URL is relative, make it absolute
        if (img.url.startsWith('/')) {
          return `${API_BASE_URL}${img.url}`;
        }
        return img.url;
      });
      
      setEmailImages(prev => [...prev, ...imageUrls]);
      toast.success(`${uploadedImages.length} slika uspešno upload-ovano`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error(error.response?.data?.message || 'Greška pri upload-u slika');
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    setEmailImages(prev => prev.filter((_, i) => i !== index));
  };

  const insertImageInBody = (imageUrl) => {
    const imgTag = `<img src="${imageUrl}" alt="Screenshot" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
    setEmailBody(prev => prev + '\n\n' + imgTag);
  };

  const handleSendEmail = async (groupId, type) => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error('Naslov i sadržaj emaila su obavezni');
      return;
    }

    const sendingKey = `${groupId}-${type}`;
    if (type === 'first') {
      setSendingFirstEmail(prev => ({ ...prev, [groupId]: true }));
    } else {
      setSendingFollowUp(prev => ({ ...prev, [groupId]: true }));
    }

    try {
      // Prepare email body with images
      let finalBody = emailBody;
      
      // If images are uploaded but not inserted in body, add them at the end
      if (emailImages.length > 0) {
        const { API_BASE_URL } = await import('../../../config/api.js');
        const imageTags = emailImages.map((imageUrl, index) => {
          // Use relative path for backend processing
          const relativePath = imageUrl.replace(API_BASE_URL, '');
          return `<img src="${relativePath}" alt="Screenshot ${index + 1}" style="max-width: 100%; height: auto; margin: 10px 0; border-radius: 8px;" />`;
        }).join('\n');
        
        // Only add images if they're not already in the body
        if (!emailBody.includes('<img')) {
          finalBody = emailBody + '\n\n' + imageTags;
        }
      }
      
      // Send images array (relative URLs for backend processing)
      const { API_BASE_URL } = await import('../../../config/api.js');
      const imageUrlsForBackend = emailImages.map(url => {
        // Convert full URL to relative path for backend
        return url.replace(API_BASE_URL, '');
      });
      
      // Send images array (URLs) with email
      let response;
      if (type === 'first') {
        response = await sendFirstEmailToEmailGroup(groupId, emailSubject, finalBody, imageUrlsForBackend);
      } else {
        response = await sendFollowUpEmailToEmailGroup(groupId, emailSubject, finalBody, imageUrlsForBackend);
      }
      
      // Show appropriate message based on results
      const sentCount = response.data?.sentCount || 0;
      const failedCount = response.data?.failedCount || 0;
      const errors = response.data?.errors || [];
      
      if (sentCount > 0 && failedCount === 0) {
        toast.success(`${type === 'first' ? 'Prvi' : 'Follow-up'} email je uspešno poslat na ${sentCount} adresa`);
      } else if (sentCount > 0 && failedCount > 0) {
        toast.warning(`${type === 'first' ? 'Prvi' : 'Follow-up'} email poslat na ${sentCount} adresa, ${failedCount} neuspešno`);
        if (errors.length > 0) {
          console.error('Email errors:', errors);
        }
      } else if (sentCount === 0 && failedCount > 0) {
        toast.error(`Greška: Nijedan email nije poslat. ${failedCount} neuspešno.`);
        if (errors.length > 0) {
          console.error('Email errors:', errors);
          // Show first few errors
          const errorMessages = errors.slice(0, 3).map(e => e.error).join(', ');
          toast.error(`Greške: ${errorMessages}${errors.length > 3 ? '...' : ''}`);
        }
      }
      setShowSendEmailModal(false);
      setEmailSubject('');
      setEmailBody('');
      setEmailImages([]);
      setSelectedGroup(null); // Reset selected group
      
      // Reset sending states
      if (type === 'first') {
        setSendingFirstEmail(prev => ({ ...prev, [groupId]: false }));
      } else {
        setSendingFollowUp(prev => ({ ...prev, [groupId]: false }));
      }
      
      // Refresh groups and stats
      await fetchGroups(); // Wait for groups to refresh
      fetchStats();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.message || 'Greška pri slanju emaila');
      
      // Reset sending states on error too
      if (type === 'first') {
        setSendingFirstEmail(prev => ({ ...prev, [groupId]: false }));
      } else {
        setSendingFollowUp(prev => ({ ...prev, [groupId]: false }));
      }
    }
  };

  const openSendEmailModal = (groupId, type) => {
    setSelectedGroup(groups.find(g => g._id === groupId));
    setEmailType(type);
    setEmailSubject('');
    setEmailBody('');
    setEmailImages([]);
    setShowSendEmailModal(true);
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Nacrt',
      ready: 'Spremno',
      first_sent: 'Prvi email poslat',
      followup_sent: 'Follow-up poslat',
      completed: 'Završeno'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'text-gray-400',
      ready: 'text-blue-400',
      first_sent: 'text-yellow-400',
      followup_sent: 'text-green-400',
      completed: 'text-green-500'
    };
    return colors[status] || 'text-gray-400';
  };

  const filteredGroups = groups.filter(group => {
    const matchesSearch = !searchTerm || 
      group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.groupNumber.toString().includes(searchTerm);
    const matchesStatus = !filterStatus || group.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Mejl Adrese
          </h1>
          <p className="text-gray-400 mt-1">Upravljanje statičnim grupama email adresa</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="h-4 w-4" />
          Nova Grupa
        </button>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Ukupno Grupa</p>
                <p className="text-2xl font-bold text-white">{stats.totalGroups}</p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Ukupno Email Adresa</p>
                <p className="text-2xl font-bold text-white">{stats.totalEmails}</p>
              </div>
              <Mail className="h-8 w-8 text-green-400" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Poslato Emailova</p>
                <p className="text-2xl font-bold text-white">
                  {stats.totalFirstEmailsSent + stats.totalFollowUpEmailsSent}
                </p>
              </div>
              <Send className="h-8 w-8 text-yellow-400" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Registracije</p>
                <p className="text-2xl font-bold text-white">{stats.totalRegistrations}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-purple-400" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Pretraži grupe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
        >
          <option value="">Svi statusi</option>
          <option value="draft">Nacrt</option>
          <option value="ready">Spremno</option>
          <option value="first_sent">Prvi email poslat</option>
          <option value="followup_sent">Follow-up poslat</option>
          <option value="completed">Završeno</option>
        </select>
      </div>

      {/* Groups List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Nema grupa. Kreirajte novu grupu da počnete.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredGroups.map((group) => (
            <div key={group._id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-white">{group.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(group.status)}`}>
                      {getStatusLabel(group.status)}
                    </span>
                  </div>
                  {group.description && (
                    <p className="text-gray-400 text-sm mb-3">{group.description}</p>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-gray-400 text-xs">Email Adresa</p>
                      <p className="text-white font-semibold">{group.totalEmails || group.emailAddresses?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Prvi Email</p>
                      <p className="text-white font-semibold">{group.firstEmailSentCount || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Follow-up</p>
                      <p className="text-white font-semibold">{group.followUpEmailSentCount || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Odgovori</p>
                      <p className="text-white font-semibold">{group.responsesCount || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Posete Sajtu</p>
                      <p className="text-white font-semibold">{group.websiteVisitsCount || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Registracije</p>
                      <p className="text-white font-semibold">{group.registrationsCount || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => fetchGroupDetails(group._id)}
                    className="p-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition"
                    title="Detalji"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(group)}
                    className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                    title="Izmeni"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  {/* Delete button - show if no emails were successfully sent */}
                  {(!group.firstEmailSentAt || (group.firstEmailSentCount === 0 && group.firstEmailFailedCount > 0)) && 
                   (!group.followUpEmailSentAt || (group.followUpEmailSentCount === 0 && group.followUpEmailFailedCount > 0)) && (
                    <button
                      onClick={() => handleDelete(group._id)}
                      className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                      title="Obriši"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  {/* Send Email Buttons - Show if no successful emails sent or if follow-up is needed */}
                  {/* First email button - show if firstEmailSentAt is null OR if firstEmailSentCount is 0 */}
                  {(!group.firstEmailSentAt || group.firstEmailSentCount === 0) && (
                    <button
                      onClick={() => openSendEmailModal(group._id, 'first')}
                      disabled={sendingFirstEmail[group._id]}
                      className="p-2 bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50"
                      title="Pošalji prvi email"
                    >
                      {sendingFirstEmail[group._id] ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  {/* Follow-up email button - show if first email was sent successfully but follow-up wasn't */}
                  {group.firstEmailSentAt && group.firstEmailSentCount > 0 && 
                   (!group.followUpEmailSentAt || group.followUpEmailSentCount === 0) && (
                    <button
                      onClick={() => openSendEmailModal(group._id, 'followup')}
                      disabled={sendingFollowUp[group._id]}
                      className="p-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition disabled:opacity-50"
                      title="Pošalji follow-up email"
                    >
                      {sendingFollowUp[group._id] ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Group Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingGroup ? 'Izmeni Grupu' : 'Nova Grupa'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Broj Grupe *
                </label>
                <input
                  type="number"
                  name="groupNumber"
                  value={formData.groupNumber}
                  onChange={handleInputChange}
                  required
                  disabled={!!editingGroup}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Naziv
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={`Grupa ${formData.groupNumber || ''}`}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Opis
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Adrese ({formData.emailAddresses.length}/100)
                </label>
                <div className="flex gap-2 mb-2">
                  <textarea
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Unesite email adrese (jedna po liniji ili odvojene zarezom)"
                    rows={3}
                    className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={addEmailAddress}
                    disabled={!emailInput.trim() || formData.emailAddresses.length >= 100}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    Dodaj
                  </button>
                </div>
                {availableContacts.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-sm text-gray-400 cursor-pointer hover:text-white">
                      Dodaj iz liste kontakata ({availableContacts.length} dostupno)
                    </summary>
                    <div className="mt-2 max-h-40 overflow-y-auto bg-gray-700 rounded-lg p-2">
                      {availableContacts
                        .filter(contact => 
                          !formData.emailAddresses.some(e => 
                            e.email.toLowerCase() === contact.email.toLowerCase()
                          )
                        )
                        .slice(0, 50)
                        .map((contact) => (
                          <button
                            key={contact._id}
                            type="button"
                            onClick={() => addContactFromList(contact)}
                            disabled={formData.emailAddresses.length >= 100}
                            className="block w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-600 rounded disabled:opacity-50"
                          >
                            {contact.companyName} - {contact.email}
                          </button>
                        ))}
                    </div>
                  </details>
                )}
                <div className="mt-2 max-h-40 overflow-y-auto bg-gray-700 rounded-lg p-2">
                  {formData.emailAddresses.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-2">Nema email adresa</p>
                  ) : (
                    formData.emailAddresses.map((emailData, index) => (
                      <div key={index} className="flex items-center justify-between px-3 py-2 hover:bg-gray-600 rounded">
                        <span className="text-sm text-white">
                          {emailData.companyName ? `${emailData.companyName} - ` : ''}
                          {emailData.email}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeEmailAddress(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
                >
                  Otkaži
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    editingGroup ? 'Sačuvaj' : 'Kreiraj'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {showSendEmailModal && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Fixed Header */}
            <div className="p-6 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-bold text-white">
                Pošalji {emailType === 'first' ? 'Prvi' : 'Follow-up'} Email - {selectedGroup.name}
              </h2>
              <button
                onClick={() => {
                  setShowSendEmailModal(false);
                  setEmailSubject('');
                  setEmailBody('');
                  setEmailImages([]);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Scrollable Content */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Naslov *
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sadržaj *
                </label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  required
                  rows={8}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none resize-y"
                  placeholder="Možete koristiti HTML. Slike će biti automatski umetnute na kraju."
                />
              </div>
              
              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Screenshot-ovi Aplikacije ({emailImages.length}/5)
                </label>
                <div className="flex gap-2 mb-2">
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleImageUpload(e.target.files)}
                      disabled={uploadingImages || emailImages.length >= 5}
                      className="hidden"
                    />
                    <div className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 hover:bg-gray-600 transition disabled:opacity-50">
                      {uploadingImages ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Upload-uje se...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          <span>Dodaj Screenshot-ove</span>
                        </>
                      )}
                    </div>
                  </label>
                </div>
                
                {/* Image Preview */}
                {emailImages.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 bg-gray-700 rounded-lg">
                    {emailImages.map((imageUrl, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={imageUrl}
                          alt={`Screenshot ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-gray-600"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition flex items-center justify-center gap-1 rounded-lg">
                          <button
                            type="button"
                            onClick={() => insertImageInBody(imageUrl)}
                            className="opacity-0 group-hover:opacity-100 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition"
                            title="Umetni u email"
                          >
                            Umetni
                          </button>
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="opacity-0 group-hover:opacity-100 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition"
                            title="Ukloni"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Maksimum 5 slika. Svaka slika max 2MB. Slike će biti automatski umetnute u email.
                </p>
              </div>
              
              <div className="text-sm text-gray-400">
                Email će biti poslat na {selectedGroup.totalEmails || selectedGroup.emailAddresses?.length || 0} adresa.
                {emailImages.length > 0 && (
                  <span className="block mt-1 text-green-400">
                    ✓ {emailImages.length} screenshot-a će biti uključeno u email
                  </span>
                )}
              </div>
            </div>
            {/* Fixed Footer */}
            <div className="p-6 border-t border-gray-700 flex justify-end gap-2 flex-shrink-0 bg-gray-800">
              <button
                type="button"
                onClick={() => {
                  setShowSendEmailModal(false);
                  setEmailSubject('');
                  setEmailBody('');
                  setEmailImages([]);
                }}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
              >
                Otkaži
              </button>
              <button
                onClick={() => handleSendEmail(selectedGroup._id, emailType)}
                disabled={!emailSubject.trim() || !emailBody.trim() || sendingFirstEmail[selectedGroup._id] || sendingFollowUp[selectedGroup._id]}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {(sendingFirstEmail[selectedGroup._id] || sendingFollowUp[selectedGroup._id]) ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Pošalji'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Details Modal */}
      {selectedGroup && !showSendEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Detalji Grupe - {selectedGroup.name}</h2>
              <button
                onClick={() => setSelectedGroup(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-xs">Email Adresa</p>
                  <p className="text-white font-bold text-xl">{selectedGroup.totalEmails || selectedGroup.emailAddresses?.length || 0}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-xs">Prvi Email Poslat</p>
                  <p className="text-white font-bold text-xl">{selectedGroup.firstEmailSentCount || 0}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-xs">Follow-up Poslat</p>
                  <p className="text-white font-bold text-xl">{selectedGroup.followUpEmailSentCount || 0}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-xs">Odgovori</p>
                  <p className="text-white font-bold text-xl">{selectedGroup.responsesCount || 0}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-xs">Posete Sajtu</p>
                  <p className="text-white font-bold text-xl">{selectedGroup.websiteVisitsCount || 0}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-xs">Registracije</p>
                  <p className="text-white font-bold text-xl">{selectedGroup.registrationsCount || 0}</p>
                </div>
              </div>

              {/* Email Addresses */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Email Adrese</h3>
                <div className="bg-gray-700 rounded-lg p-4 max-h-60 overflow-y-auto">
                  {selectedGroup.emailAddresses?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedGroup.emailAddresses.map((emailData, index) => (
                        <div key={index} className="text-sm text-white">
                          {emailData.companyName ? `${emailData.companyName} - ` : ''}
                          {emailData.email}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">Nema email adresa</p>
                  )}
                </div>
              </div>

              {/* Responses */}
              {selectedGroup.responses && selectedGroup.responses.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Odgovori</h3>
                  <div className="bg-gray-700 rounded-lg p-4 max-h-60 overflow-y-auto">
                    <div className="space-y-2">
                      {selectedGroup.responses.map((response, index) => (
                        <div key={index} className="text-sm text-white border-b border-gray-600 pb-2">
                          <p><strong>Email:</strong> {response.email}</p>
                          <p><strong>Tip:</strong> {response.emailType}</p>
                          <p><strong>Datum:</strong> {new Date(response.responseReceivedAt).toLocaleString('sr-RS')}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Visits */}
              {selectedGroup.visits && selectedGroup.visits.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Posete Sajtu</h3>
                  <div className="bg-gray-700 rounded-lg p-4 max-h-60 overflow-y-auto">
                    <div className="space-y-2">
                      {selectedGroup.visits.map((visit, index) => (
                        <div key={index} className="text-sm text-white border-b border-gray-600 pb-2">
                          <p><strong>Email:</strong> {visit.email || 'Nepoznat'}</p>
                          <p><strong>Poslednja poseta:</strong> {new Date(visit.lastVisitAt).toLocaleString('sr-RS')}</p>
                          <p><strong>Registrovan:</strong> {visit.registeredAfterVisit ? 'Da' : 'Ne'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailGroupsView;

